/**
 * Mage profile: persistent XP and per-field highest XP tracking.
 * Source: SelectorRenderer.as (player.stageHighestXps), Calculator.as.
 */

import { levelFromXp } from "./mage-xp.js";
import { getSkillCost, canLearn } from "./skills.js";

export interface MageStats {
  totalKills: number;        // all-time monsters killed
  totalBanishes: number;     // all-time monsters reached altar (banish events)
  totalTimePlayed: number;   // ms, sum across all battles
  stageVictories: Record<string, number>; // fieldId → win count
  stageFailures: Record<string, number>;  // fieldId → fail count
  totalBattles: number;      // total started battles
  /** AS3 career StatId.MANA_GAINED_FROM_WAVESPEEDING — light from hurrying wave spawns. */
  manaFromEarlyWaveSpawns: number;
  /** AS3 WAVES_STARTED_EARLY analogue: count of successful hurry actions. */
  earlyWaveHurries: number;
}

export interface MageProfile {
  totalXp: number;
  /** fieldId → highest XP ever earned on that field. Re-runs yield only max(0, newXp - highest). */
  stageHighestXps: Record<string, number>;
  /** skillId → current level (0 if never learned). */
  skillLevels: Record<number, number>;
  stats: MageStats;
  /** IDs of achievements unlocked so far. */
  unlockedAchievements?: string[];
}

function createDefaultStats(): MageStats {
  return {
    totalKills: 0,
    totalBanishes: 0,
    totalTimePlayed: 0,
    stageVictories: {},
    stageFailures: {},
    totalBattles: 0,
    manaFromEarlyWaveSpawns: 0,
    earlyWaveHurries: 0,
  };
}

/** Merge persisted stats with defaults (new fields from older saves). */
export function mergeMageStats(raw: unknown): MageStats {
  const d = createDefaultStats();
  if (raw == null || typeof raw !== "object") return d;
  return { ...d, ...(raw as Partial<MageStats>) };
}

export function createMageProfile(): MageProfile {
  return { totalXp: 0, stageHighestXps: {}, skillLevels: {}, stats: createDefaultStats(), unlockedAchievements: [] };
}

export function incrementStatKills(profile: MageProfile, n: number = 1): void {
  profile.stats.totalKills += n;
}

export function incrementStatBanishes(profile: MageProfile, n: number = 1): void {
  profile.stats.totalBanishes += n;
}

export function addStatPlayTime(profile: MageProfile, ms: number): void {
  profile.stats.totalTimePlayed += ms;
}

export function recordStageVictory(profile: MageProfile, fieldId: string): void {
  profile.stats.stageVictories[fieldId] = (profile.stats.stageVictories[fieldId] ?? 0) + 1;
}

export function recordStageFailure(profile: MageProfile, fieldId: string): void {
  profile.stats.stageFailures[fieldId] = (profile.stats.stageFailures[fieldId] ?? 0) + 1;
}

export function incrementStatBattles(profile: MageProfile): void {
  profile.stats.totalBattles += 1;
}

export function incrementStatEarlyWaveHurryMana(profile: MageProfile, n: number): void {
  profile.stats.manaFromEarlyWaveSpawns =
    (profile.stats.manaFromEarlyWaveSpawns ?? 0) + n;
}

export function incrementStatEarlyWaveHurries(profile: MageProfile, count: number = 1): void {
  profile.stats.earlyWaveHurries = (profile.stats.earlyWaveHurries ?? 0) + count;
}

/**
 * Award XP for winning a field.
 * If earnedXp <= existing highest for this field, delta=0 (no reward for re-run without improvement).
 * Otherwise delta = earnedXp - highest. Updates profile in place.
 * Returns { delta, newTotal, newLevel }.
 */
export function awardStageXp(
  profile: MageProfile,
  fieldId: string,
  earnedXp: number
): { delta: number; newTotal: number; newLevel: number } {
  const highest = profile.stageHighestXps[fieldId] ?? 0;
  const delta = Math.max(0, earnedXp - highest);
  if (delta > 0) {
    profile.stageHighestXps[fieldId] = earnedXp;
    profile.totalXp += delta;
  }
  return {
    delta,
    newTotal: profile.totalXp,
    newLevel: levelFromXp(profile.totalXp),
  };
}

/**
 * Total skill points spent = sum of getSkillCost(id, 1..level) for each learned skill.
 */
export function totalSkillPointsSpent(profile: MageProfile): number {
  let total = 0;
  for (const [idStr, level] of Object.entries(profile.skillLevels)) {
    const skillId = Number(idStr);
    for (let lv = 1; lv <= level; lv++) {
      total += getSkillCost(skillId, lv);
    }
  }
  return total;
}

/**
 * Available skill points = mageLevel - totalSpent (clamped to >= 0).
 * Points are derived, not stored explicitly.
 */
export function availableSkillPoints(profile: MageProfile, mageLevel: number): number {
  return Math.max(0, mageLevel - totalSkillPointsSpent(profile));
}

/**
 * Increment skill level by 1 if allowed (mage level check, points check, max level check).
 * Returns true if the skill was learned, false otherwise.
 */
export function tryLearnSkill(
  profile: MageProfile,
  skillId: number,
  mageLevel: number
): boolean {
  const points = availableSkillPoints(profile, mageLevel);
  if (!canLearn(profile.skillLevels, skillId, mageLevel, points)) return false;
  profile.skillLevels[skillId] = (profile.skillLevels[skillId] ?? 0) + 1;
  return true;
}

/** Decrement a learned skill by 1 level, deleting the save key at level 0. */
export function tryRefundSkill(profile: MageProfile, skillId: number): boolean {
  const current = profile.skillLevels[skillId] ?? 0;
  if (current <= 0) return false;
  if (current === 1) {
    delete profile.skillLevels[skillId];
  } else {
    profile.skillLevels[skillId] = current - 1;
  }
  return true;
}

// Re-export for convenience (skill domain functions needed by callers of mage-profile)
export { SKILL_SPECS, getSkillCost, getSkillEffectiveValue, getSkillPlayerLevelReq, canLearn } from "./skills.js";
